import React from 'react';
import classnames from 'classnames';
import _get from 'lodash/get';
import { BASE_FIELD_PAGE, BASE_FIELD_PAGE_COLLECTION } from 'pages/Attributes/consts';

import Tooltip from '@material-ui/core/Tooltip';
import CheckCircle from '@material-ui/icons/CheckCircle';
import Lock from '@material-ui/icons/Lock';
import NotInterested from '@material-ui/icons/NotInterested';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';

const headerAttributes = ['user_firstName', 'user_lastName', 'user_title', 'user_anniversaryDate'];
const USER_EDITABILITY_NONE = 'NONE';
const USER_EDITABILITY_FULL = 'FULL';
const USER_EDITABILITY_PARTIAL = 'PARTIAL';
const LOCKED_TOOLTIP_TITLE = 'This is a Sift default attribute and may not be edited.';
const getDataType = (params, globalConfig) => {
  const baseField = _get(params, 'value.baseField');
  return _get(globalConfig, `fields.${baseField}.displayName`);
};
const getClassNames = (params, classes) => {
  const field = _get(params, 'value');
  return classnames(classes.cell, {
    [classes.disabled]: _get(field, 'connectDisabled', false),
    [classes.noClick]:
      !_get(field, 'editable', false) &&
      !_get(field, 'deletable', false) &&
      ![BASE_FIELD_PAGE, BASE_FIELD_PAGE_COLLECTION].includes(_get(field, 'baseField')),
  });
};

const isLocked = params => {
  const field = _get(params, ['row', 'displayName']);
  return (
    !_get(field, 'editable') &&
    !_get(field, 'deletable') &&
    ![BASE_FIELD_PAGE, BASE_FIELD_PAGE_COLLECTION].includes(_get(field, 'baseField'))
  );
};

const renderLockedTooltipCell = params => {
  if (isLocked(params)) {
    return (
      <Tooltip title={LOCKED_TOOLTIP_TITLE}>
        <div>{params.value}</div>
      </Tooltip>
    );
  } else {
    return params.value;
  }
};

export const attributeColumns = (globalConfig, classes, profileAttributes, terminology) => [
  {
    field: 'id',
    headerName: 'ID',
    type: 'number',
    hide: true,
    filterable: false,
  },
  {
    cellClassName: params => getClassNames(params, classes),
    field: 'displayName',
    headerName: 'Name',
    type: 'string',
    flex: 1,
    renderCell: params => renderLockedTooltipCell(params),
    valueGetter: params => {
      return _get(params, 'value.displayName', 'display name');
    },
  },
  {
    cellClassName: params => getClassNames(params, classes),
    field: 'type',
    headerName: 'Data Type',
    type: 'string',
    flex: 1,
    renderCell: params => renderLockedTooltipCell(params),
    valueGetter: params => getDataType(params, globalConfig),
    sortComparator: (v1, v2, cellParams1, cellParams2) =>
      getDataType(cellParams1, globalConfig).localeCompare(getDataType(cellParams2, globalConfig)),
  },
  {
    cellClassName: params => getClassNames(params, classes),
    field: 'onProfile',
    headerName: 'On Profile',
    type: 'string',
    flex: 1,
    filterable: false,
    description: `If the attribute is on the ${terminology.singular} profile page`,
    renderCell: params => {
      if (params.value === 'on profile') {
        return (
          <Tooltip title={isLocked(params) ? `On Profile. ${LOCKED_TOOLTIP_TITLE}` : 'On Profile'}>
            <CheckCircle color="secondary" />
          </Tooltip>
        );
      } else if (params.value === 'not on profile') {
        return (
          <Tooltip
            title={isLocked(params) ? `Not On Profile. ${LOCKED_TOOLTIP_TITLE}` : 'Not On Profile'}
          >
            <NotInterested className={classes.grayIcon} />
          </Tooltip>
        );
      } else {
        return (
          <Tooltip title={isLocked(params) ? `Locked. ${LOCKED_TOOLTIP_TITLE}` : 'Locked'}>
            <Lock color="secondary" />
          </Tooltip>
        );
      }
    },
    valueGetter: params => {
      if (
        !_get(params, 'value.connectDisabled', false) &&
        profileAttributes.includes(_get(params, 'value.objectKey'))
      ) {
        if (headerAttributes.includes(_get(params, 'value.id'))) {
          return 'locked';
        }
        return 'on profile';
      }
      if (
        _get(params, 'value.connectDisabled', false) ||
        !profileAttributes.includes(_get(params, 'value.objectKey'))
      ) {
        return 'not on profile';
      }
    },
  },
  {
    cellClassName: params => getClassNames(params, classes),
    field: 'restrictionAndAccess',
    headerName: 'Restriction/Access',
    type: 'string',
    flex: 1,
    description: `If the attribute is restricted to certain ${terminology.plural} or has access controls.`,
    renderCell: params => {
      if (params.row.restrictionAndAccess.restricted) {
        return (
          <Tooltip title={isLocked(params) ? `Restricted. ${LOCKED_TOOLTIP_TITLE}` : 'Restricted'}>
            <div>
              <FiberManualRecordIcon
                className={classnames(classes.circleIcon, classes.redCircleIcon)}
              />
              Restricted
            </div>
          </Tooltip>
        );
      } else {
        return (
          <Tooltip title={isLocked(params) ? `Public. ${LOCKED_TOOLTIP_TITLE}` : 'Public'}>
            <div>
              <FiberManualRecordIcon
                className={classnames(classes.circleIcon, classes.greenCircleIcon)}
              />
              Public
            </div>
          </Tooltip>
        );
      }
    },
    valueGetter: params => {
      return _get(params, ['row', 'restrictionAndAccess', 'restricted'], false);
    },
  },
  {
    cellClassName: params => getClassNames(params, classes),
    field: 'userEditing',
    headerName: 'User Editing',
    filterable: false,
    type: 'string',
    flex: 1,
    description: `If ${terminology.plural} can edit the attribute. Partially editable means that the attribute is mapped from at least one data source, so will not be editable to those ${terminology.plural}.`,
    renderCell: params => {
      if (params.value === 'not editable') {
        return (
          <Tooltip
            title={isLocked(params) ? `Not Editable. ${LOCKED_TOOLTIP_TITLE}` : 'Not Editable'}
          >
            <div>
              <FiberManualRecordIcon className={classnames(classes.circleIcon, classes.grayIcon)} />
              Not Editable
            </div>
          </Tooltip>
        );
      } else if (params.value === 'partially editable') {
        return (
          <Tooltip
            title={
              isLocked(params)
                ? `Partially Editable. ${LOCKED_TOOLTIP_TITLE}`
                : 'Partially Editable'
            }
          >
            <div>
              <FiberManualRecordIcon className={classes.circleIcon} color="secondary" /> Partially
              Editable
            </div>
          </Tooltip>
        );
      } else if (params.value === 'editable') {
        return (
          <Tooltip title={isLocked(params) ? `Editable. ${LOCKED_TOOLTIP_TITLE}` : 'Editable'}>
            <div>
              <FiberManualRecordIcon
                className={classnames(classes.circleIcon, classes.greenCircleIcon)}
              />
              Editable
            </div>
          </Tooltip>
        );
      }
    },
    valueGetter: params => {
      if (_get(params, 'value._userEditability') === USER_EDITABILITY_NONE) {
        return 'not editable';
      } else if (_get(params, 'value._userEditability') === USER_EDITABILITY_FULL) {
        return 'editable';
      } else if (_get(params, 'value._userEditability') === USER_EDITABILITY_PARTIAL) {
        return 'partially editable';
      }
    },
  },
  {
    field: 'enabled',
    headerName: 'Enabled',
    type: 'string',
    flex: 0.5,
    filterable: false,
    description: 'If the attribute is visible anywhere on Sift.',
    renderCell: params => {
      if (params.value === 'locked') {
        return (
          <Tooltip title={isLocked(params) ? `Locked. ${LOCKED_TOOLTIP_TITLE}` : 'Locked'}>
            <Lock color="secondary" />
          </Tooltip>
        );
      } else if (params.value === 'enabled') {
        return (
          <Tooltip title={isLocked(params) ? `Enabled. ${LOCKED_TOOLTIP_TITLE}` : 'Enabled'}>
            <CheckCircle color="secondary" />
          </Tooltip>
        );
      } else {
        return (
          <Tooltip
            title={isLocked(params) ? `Not Enabled. ${LOCKED_TOOLTIP_TITLE}` : 'Not Enabled'}
          >
            <NotInterested color="disabled" />
          </Tooltip>
        );
      }
    },
    valueGetter: params => {
      if (
        !_get(params, 'value.connectDisabled', false) &&
        !_get(params, 'value.deletable', false)
      ) {
        return 'locked';
      } else if (
        !_get(params, 'value.connectDisabled', false) &&
        _get(params, 'value.deletable', false)
      ) {
        return 'enabled';
      } else {
        return 'not enabled';
      }
    },
    cellClassName: params => getClassNames(params, classes),
  },
];

export const attributeRows = parentFields =>
  parentFields.map((field, index) => ({
    id: _get(field, 'id', index),
    displayName: field,
    type: field,
    onProfile: field,
    restrictionAndAccess: field,
    userEditing: field,
    enabled: field,
  }));
